import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { orpc, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import type {
  UnignoreAPInvoicesInput,
  UnignoreAPInvoicesResponse,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';
import { invalidateTaskQueries } from '@/api/rpc/hooks/tasks/invalidateTaskQueries';
import { buildUnignoreResultToast } from './unignoreInvoiceToasts';

export interface UseUnignoreAPInvoicesOptions {
  onSuccess?: (
    data: UnignoreAPInvoicesResponse,
    variables: UnignoreAPInvoicesInput,
  ) => void;
}

export function useUnignoreAPInvoices(options?: UseUnignoreAPInvoicesOptions) {
  const gt = useGT();
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((orpc) => [
    orpc.accountsPayable.invoices.list.queryKey({ input: {} }),
    orpc.accountsPayable.invoices.get.key(),
  ]);
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation(
    orpc.accountsPayable.invoices.unignore.mutationOptions({
      onSuccess: (data, variables) => {
        showToast(buildUnignoreResultToast(gt, data));
        invalidateRpcQueryFn({});
        invalidateTaskQueries(queryClient);
        options?.onSuccess?.(data, variables);
      },
      onError: (error) => {
        showToast({
          title: gt('Error', {
            context: 'toast title when unignore invoices request fails',
          }),
          message:
            error.message ||
            gt('Failed to unignore invoices', {
              context: 'toast when the unignore invoices request fails',
            }),
          color: 'error',
        });
      },
    }),
  );
}
